import { getGetOpportunityByIdQueryOptions } from "@fp/admin-api-client/client";
import { AdminPageHeader } from "@/components/admin-page-header";
import type { OpportunityDto } from "@fp/admin-api-client/types/opportunityDto.js";
import { PayRateType } from "@fp/admin-api-client/types/payRateType.js";
import { Button } from "@fp/ui/components/base/buttons/button";
import {
	PageContentContainer,
	PageLoading,
	PageProgressBar,
} from "@fp/ui/components/base/page-layout/page-layout";
import { AdminLayout } from "@/components/admin-layout";
import {
	RateForm,
	RateFormContent,
	RateFormErrorMessage,
	type RateFormContext,
} from "@fp/ui/components/opportunities/opportunity-set-rate-form";

import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";

interface SetRatesPageProps {
	opportunity: OpportunityDto;
	onSuccess: (opportunity: OpportunityDto) => Promise<void>;
}

export const SetRatesPageContent = ({
	opportunity,
	onSuccess,
}: SetRatesPageProps) => {
	const defaultValues = useMemo(() => {
		return {
			id: opportunity.id,
			rate: opportunity?.payRate?.value?.toString() || "",
			rateType: opportunity?.payRate?.type || PayRateType.Hourly,
			useNumberOfUnits: (opportunity?.numberOfUnits ?? 0) > 0,
			numberOfUnits: opportunity?.numberOfUnits?.toString() || "",
			useUnitsPerPerson: (opportunity?.maxUnitsPerParticipant ?? 0) > 0,
			unitsPerPerson: opportunity?.maxUnitsPerParticipant?.toString() || "",
		};
	}, [opportunity]);

	return (
		<RateForm defaultValues={defaultValues} onSuccess={onSuccess}>
			{({ handleSubmit, transition }: RateFormContext) => (
				<>
					<RateFormContent />
					<RateFormErrorMessage />

					<div className="flex-1 flex items-end pt-6">
						<Button
							isDisabled={transition}
							type="button-primary"
							onClick={handleSubmit}
							className="h-16 w-full"
						>
							Set Participation Settings
						</Button>
					</div>
				</>
			)}
		</RateForm>
	);
};

export const Route = createFileRoute("/opps/$oppId/edit/set-rates")({
	component: RouteComponent,
	loader: async ({ params, context: { queryClient } }) => {
		const opportunity = await queryClient.fetchQuery(
			getGetOpportunityByIdQueryOptions(params.oppId),
		);
		return opportunity.data;
	},
	pendingComponent: () => <PageLoading />,
});

function RouteComponent() {
	const opportunity: OpportunityDto = Route.useLoaderData();
	const router = useRouter();
	const { oppId } = Route.useParams();

	const onSuccess = useCallback(
		async (opportunity: OpportunityDto) => {
			await router.invalidate({ sync: true });
			router.navigate({
				to: "/opps/$oppId/edit/participation",
				params: { oppId: opportunity.id },
			});
		},
		[router],
	);

	return (
		<AdminLayout>
			<AdminPageHeader
				title="Set Pay Rate"
				parent={{
					url: router.buildLocation({ to: "/opps/new", search: { oppId } }).href,
					label: "Create an Opp",
				}}
				footer={<PageProgressBar current={1} total={4} />}
			/>
			<PageContentContainer className="space-y-6">
				<SetRatesPageContent opportunity={opportunity} onSuccess={onSuccess} />
			</PageContentContainer>
		</AdminLayout>
	);
}
